/**
 * Mehr-Standort Phase 4 (· 273c) — generischer Filial-Umschalter (nur Hauptsitz).
 *
 * Wiederverwendbar für Verkauf (Disposition) und Buchhaltung: schaltet den
 * Daten-Kontext per ``X-Org-Branch``-Header (`setApiOrgBranch`) auf eine Filiale
 * um und meldet die Auswahl an die Seite zurück (`onChange`).
 *
 * · 279 — `allowAll` zeigt zusätzlich „Alle Standorte (Aggregat)"; `allowReorder`
 * erlaubt das Sortieren der Standorte per Drag & Drop (in localStorage gespeichert).
 */
import { useEffect, useState } from "react";
import { Building2, ChevronDown, GripVertical, ArrowUpDown, X } from "lucide-react";
import api, { setApiOrgBranch } from "../../lib/api";

const ORDER_KEY = "tw_branch_order";

function loadOrder() {
    try { return JSON.parse(localStorage.getItem(ORDER_KEY) || "[]"); } catch { return []; }
}
function saveOrder(ids) {
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(ids)); } catch { /* ignore */ }
}

function sortBranches(list, order) {
    const self = list.filter((b) => b.is_self);
    const rest = list.filter((b) => !b.is_self);
    rest.sort((a, b) => {
        const ia = order.indexOf(a.dealer_id), ib = order.indexOf(b.dealer_id);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });
    return [...self, ...rest];
}

export default function OrgBranchSwitcher({ user, value, onChange, onLabelChange, dark = false, allowAll = true, allowReorder = false }) {
    const [branches, setBranches] = useState([]);
    const [order, setOrder] = useState(loadOrder());
    const [reorderOpen, setReorderOpen] = useState(false);
    const [dragId, setDragId] = useState(null);
    const isHauptsitz = user?.branch_role === "hauptsitz";

    useEffect(() => {
        if (!isHauptsitz) return;
        api.get("/org/locations")
            .then((r) => setBranches(r.data?.locations || []))
            .catch(() => setBranches([]));
    }, [isHauptsitz]);

    // Aktuellen Standort-Label an die Seite melden (für QR-Druck „aktueller Standort").
    useEffect(() => {
        if (!onLabelChange || branches.length === 0) return;
        const cur = value
            ? branches.find((b) => b.dealer_id === value)
            : branches.find((b) => b.is_self);
        if (cur) onLabelChange([cur.site_code, cur.company_name].filter(Boolean).join(" · "));
    }, [branches, value, onLabelChange]);

    if (!isHauptsitz || branches.length <= 1) return null;

    const sorted = sortBranches(branches, order);

    const handle = (e) => {
        const v = e.target.value;
        const branchId = v === "self" ? null : v;  // "self" → Hauptsitz, "__all__" → Aggregat
        setApiOrgBranch(branchId);
        if (branchId) sessionStorage.setItem("tw_scope_branch", branchId);
        else sessionStorage.removeItem("tw_scope_branch");
        onChange?.(branchId);
    };

    const onDrop = (targetId) => {
        if (!dragId || dragId === targetId) return;
        const ids = sorted.filter((b) => !b.is_self).map((b) => b.dealer_id);
        const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
        if (from === -1 || to === -1) return;
        ids.splice(to, 0, ids.splice(from, 1)[0]);
        setOrder(ids);
        saveOrder(ids);
        setDragId(null);
    };

    const cls = dark
        ? "appearance-none h-9 pl-3 pr-8 rounded-sm border border-gray-600 bg-[#111827] text-white text-xs font-bold uppercase tracking-wider cursor-pointer hover:border-[#FFCC00]"
        : "appearance-none h-9 pl-3 pr-8 rounded-sm border border-gray-300 bg-white text-[#111827] text-xs font-bold uppercase tracking-wider cursor-pointer hover:border-[#FFCC00]";

    return (
        <div className="inline-flex items-center gap-1.5 relative" data-testid="org-branch-switcher">
            <Building2 className={`w-4 h-4 ${dark ? "text-[#FFCC00]" : "text-[#111827]"}`} />
            <div className="relative">
                <select value={value || "self"} onChange={handle} className={cls} data-testid="org-branch-select" aria-label="Standort/Filiale wählen">
                    {allowAll && <option value="__all__">▦ Alle Standorte</option>}
                    {sorted.map((b) => (
                        <option key={b.dealer_id} value={b.is_self ? "self" : b.dealer_id}>
                            {`${b.site_code} · ${b.street || b.company_name}${b.is_self ? " ★" : ""}`}
                        </option>
                    ))}
                </select>
                <ChevronDown className={`w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none ${dark ? "text-gray-400" : "text-gray-500"}`} />
            </div>

            {allowReorder && (
                <button onClick={() => setReorderOpen((o) => !o)} title="Standorte sortieren"
                    className={`h-9 px-2 rounded-sm border ${dark ? "border-gray-600 text-gray-300 hover:border-[#FFCC00]" : "border-gray-300 text-gray-600 hover:border-[#FFCC00]"}`}
                    data-testid="org-branch-reorder-toggle">
                    <ArrowUpDown className="w-4 h-4" />
                </button>
            )}

            {reorderOpen && (
                <div className="absolute top-11 right-0 z-50 w-72 bg-white border border-gray-200 rounded-sm shadow-lg p-3" data-testid="org-branch-reorder-panel">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Standorte sortieren</span>
                        <button onClick={() => setReorderOpen(false)} data-testid="org-branch-reorder-close"><X className="w-4 h-4 text-gray-500" /></button>
                    </div>
                    <p className="text-[11px] text-gray-400 mb-2">Ziehen zum Umsortieren. Der eigene Sitz bleibt oben.</p>
                    <div className="space-y-1">
                        {sorted.filter((b) => !b.is_self).map((b) => (
                            <div key={b.dealer_id}
                                draggable
                                onDragStart={() => setDragId(b.dealer_id)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => onDrop(b.dealer_id)}
                                className={`flex items-center gap-2 px-2 py-2 rounded-sm border cursor-grab active:cursor-grabbing ${dragId === b.dealer_id ? "border-[#FFCC00] bg-[#FFCC00]/10" : "border-gray-200 bg-gray-50 hover:bg-gray-100"}`}
                                data-testid={`org-branch-reorder-item-${b.dealer_id}`}>
                                <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
                                <span className="text-sm font-bold text-[#111827] truncate">{b.site_code} · {b.street || b.company_name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
